import React, { useCallback, useRef, useState } from 'react'
import {
  Button,
  colors,
  Checkbox,
  DialogOverlay,
  DialogOverlayFooter,
  DialogOverlayActions,
  Row,
  Separator,
  Paragraph,
  H2,
} from '@fs/zion-ui'
import { NoticeWarning } from '@fs/zion-icon'
import { Trans, useTranslation } from 'react-i18next'

const AdvancedRecordDetectionModal = ({ advancedRecordDetectionOverlay, onSkip, onProceed }) => {
  const [doNotShowAgain, setDoNotShowAgain] = useState(false)
  const { t } = useTranslation()

  const handleProceed = () => {
    if (doNotShowAgain) {
      localStorage.setItem('rip:doNotShowAdvancedRecordDetectionModal', 'true')
      localStorage.setItem('rip:advancedRecordDetectionChoice', 'proceed')
    }
    onProceed()
  }

  const handleSkip = () => {
    if (doNotShowAgain) {
      localStorage.setItem('rip:doNotShowAdvancedRecordDetectionModal', 'true')
      localStorage.setItem('rip:advancedRecordDetectionChoice', 'skip')
    }
    onSkip()
  }

  return (
    <DialogOverlay
      {...advancedRecordDetectionOverlay}
      title={
        <>
          <Separator size="xxs" />
          <Row>
            <NoticeWarning color={colors.yellow90} background={colors.yellow10} />
            <H2 size="H5">{t('advanced-record-detection.title', 'Advanced Record Detection')}</H2>
          </Row>
        </>
      }
      size="md"
    >
      <div>
        <Paragraph size="md">
          <Trans i18nKey="advanced-record-detection.body-1">
            This record spans over <b>more than one page</b>. We recommend <b>some indexing or genealogy</b> experience
            to fix these families.
          </Trans>
        </Paragraph>

        <Separator size="xxs" />
        <Paragraph size="md">{t('advanced-record-detection.body-2', 'Proceed with this record?')}</Paragraph>
        <Separator size="xxs" />

        <Checkbox
          label={<Paragraph size="sm">Remember my choice</Paragraph>}
          name="Remember my choice"
          checked={doNotShowAgain}
          onChange={() => setDoNotShowAgain(!doNotShowAgain)}
        />
      </div>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button onClick={handleSkip} data-testid="skip-button" linkName="skipButton">
            {t('advanced-record-detection.skip-button', 'Skip This Record')}
          </Button>
          <Button onClick={handleProceed} emphasis="high" data-testid="proceed-button" linkName="proceedButton">
            {t('advanced-record-detection.proceed-button', 'Proceed')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

// Keeps track of the record ID for which the modal has been shown.
// Allows for the modal to be shown only once per record
function useAdvancedRecordDetectionModalDetails() {
  const currentlyTrackedRecordId = useRef('unset')

  const hasShownModalForRecordId = useCallback(({ recordId }) => {
    if (currentlyTrackedRecordId.current === 'unset') return false
    return currentlyTrackedRecordId.current === recordId
  }, [])

  const setHasShownModalForRecordId = useCallback(
    ({ recordId }) => {
      if (currentlyTrackedRecordId.current !== recordId) {
        currentlyTrackedRecordId.current = recordId
        hasShownModalForRecordId.current = true
      }
    },
    [hasShownModalForRecordId]
  )

  return {
    hasShownModalForRecordId,
    setHasShownModalForRecordId,
  }
}

export default AdvancedRecordDetectionModal
export { useAdvancedRecordDetectionModalDetails }
